import { InputAdornment, TextField } from '@material-ui/core';
import { useStyles } from './styles';
import ChatSendIcon from '../../assets/svg/chat_send.svg';
import { IMessages } from '.';
import styles from './styles.module.scss';
import { LanguageCode } from '__generated__/globalTypes';
import { useEffect, useMemo, useRef } from 'react';
import useReceivedAssistantMessage from './hooks/useReceivedAssistantMessage';
import AmiraFilter from './AmiraFilter';

type IAmiraFooter = {
    handleTextChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    answerLoading: boolean;
    message: string;
    pendingMessages: IMessages[];
    setPendingMessages: (value: []) => void;
    sendMessage: (value: {}) => void;
    storeSupportedLanguages: LanguageCode[];
    conversationLanguage?: LanguageCode | null;
    focusInput: boolean;
    setFocusInput: (value: boolean) => void;
    isCollectingFeedback?: boolean;
    isSuggestionMessageDisplay: () => boolean;
};
export const AmiraFooter = (props: IAmiraFooter) => {
    const {
        handleTextChange,
        answerLoading,
        message,
        pendingMessages,
        setPendingMessages,
        sendMessage,
        storeSupportedLanguages,
        conversationLanguage,
        focusInput,
        setFocusInput,
        isCollectingFeedback,
        isSuggestionMessageDisplay,
    } = props;
    const { isReceivedAssistantMessage, setIsReceivedAssistantMessage } = useReceivedAssistantMessage();
    const classes = useStyles();
    const showBlink = useMemo(
        () => !storeSupportedLanguages?.length || storeSupportedLanguages?.length === 2,
        [storeSupportedLanguages],
    );
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const handleBlur = () => {
            if (!message.length && !isCollectingFeedback) {
                setFocusInput(false);
            }
        };
        if (inputRef.current) {
            if (focusInput) inputRef.current.focus();
            inputRef.current.addEventListener('blur', handleBlur);
        }
        return () => {
            if (inputRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                inputRef.current.removeEventListener('blur', handleBlur);
            }
        };
    }, [focusInput, setFocusInput, message, isCollectingFeedback]);

    const handleSendMessage = () => {
        if (!answerLoading && message?.trim() && !isReceivedAssistantMessage) {
            if (pendingMessages.length) {
                setPendingMessages([]);
            }
            if (isReceivedAssistantMessage) {
                setIsReceivedAssistantMessage(false);
            }
            setFocusInput(false);
            const isIncludeRequestParameter = !focusInput;
            sendMessage({ isIncludeRequestParameter });
        } else if (isCollectingFeedback && !message?.trim()?.length) {
            setFocusInput(false);
            sendMessage({});
        }
    };

    const inputClasses = useMemo(() => {
        const focusClass = focusInput ? (isCollectingFeedback ? 'feedback-reason' : 'other-option') : null;
        let languageClass;

        if (conversationLanguage) {
            languageClass = conversationLanguage;
        } else if (showBlink) {
            languageClass = 'both';
        } else {
            languageClass = storeSupportedLanguages[0];
        }

        if (focusClass) {
            return styles[`amiraInput__supported-language-${focusClass}-${languageClass}`];
        }

        return styles[`amiraInput__supported-language-${languageClass}`];
    }, [conversationLanguage, focusInput, isCollectingFeedback, showBlink, storeSupportedLanguages]);

    return (
        <div
            className={classes.footerContainer}
            style={{ display: isReceivedAssistantMessage || answerLoading ? 'none' : '' }}
        >
            <div className={classes.footerShadowContainer}>
                {isSuggestionMessageDisplay() ? <AmiraFilter /> : null}
                <div className={`${styles.amiraInput} ${inputClasses} ${classes.customTextField}`}>
                    <TextField
                        className={classes.textField}
                        multiline
                        maxRows={4}
                        placeholder=" "
                        onChange={(e) => {
                            handleTextChange(e);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                handleSendMessage();
                                e.preventDefault(); // prevent adding a newline
                            }
                        }}
                        disabled={answerLoading}
                        value={message}
                        style={{ display: isReceivedAssistantMessage || answerLoading ? 'none' : '' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    onClick={() => handleSendMessage()}
                                    style={{ cursor: answerLoading ? 'not-allowed' : 'pointer' }}
                                >
                                    <img src={ChatSendIcon} alt="Taffi" height={30} width={30} />
                                </InputAdornment>
                            ),
                            inputRef,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
